/** Bounded first-party page receipts. Never send paths, queries, names or research. */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionToken } from '@/auth/session';

export function measurementAllowed() {
  try {
    return navigator.doNotTrack !== '1' && window.doNotTrack !== '1' && navigator.globalPrivacyControl !== true &&
      localStorage.getItem('bds_analytics_opt_out') !== 'true';
  } catch { return false; }
}

export function measurementPage(path) {
  if (path === '/') return 'landing';
  const roots = {overview:'overview', monitor:'monitor', wire:'monitor', research:'research', company:'research', asset:'research', markets:'markets', ask:'ask', dealflow:'dealflow', 'private-market':'dealflow'};
  return roots[path.split('/')[1]] || null;
}

function identity(storage, key, maxAge) {
  const now = Date.now();
  let saved;
  try { saved = JSON.parse(storage.getItem(key) || 'null'); } catch { saved = null; }
  if (!saved || typeof saved.id !== 'string' || !Number.isFinite(saved.at) || now-saved.at > maxAge) {
    saved = {id:crypto.randomUUID(), at:now};
  }
  // Session time is sliding; visitor lifetime is fixed.
  if (key.endsWith('session')) saved.at=now;
  storage.setItem(key, JSON.stringify(saved));
  return saved.id;
}

export default function BusinessActivity() {
  const location = useLocation();
  const token = useSessionToken();
  const sent = useRef(null);
  useEffect(() => {
    const page=measurementPage(location.pathname);
    if (!page || !measurementAllowed() || (page !== 'landing' && !token)) return undefined;
    // No auth query/fragment, admin page or private subject is captured.
    if (location.hash || /(?:token|session_id|code)=/i.test(location.search)) return undefined;
    const marker=page+'|'+(token || 'anonymous');
    const timer=setTimeout(() => {
      if (sent.current===marker) return;
      try {
        const payload={event_id:crypto.randomUUID(), visitor_id:identity(localStorage,'bds_business_visitor',30*86400000),
          session_id:identity(sessionStorage,'bds_business_session',30*60000),page};
        sent.current=marker;
        const controller=new AbortController();
        const deadline=setTimeout(() => controller.abort(),3000);
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/activity`,{
          method:'POST', headers:{'Content-Type':'application/json', ...(token?{Authorization:`Bearer ${token}`}:{})},
          body:JSON.stringify(payload), signal:controller.signal, credentials:'include',
        }).catch(() => {}).finally(() => clearTimeout(deadline));
      } catch { /* Measurement is optional and must not interrupt use. */ }
    },0);
    return () => clearTimeout(timer);
  },[location.pathname,location.search,location.hash,token]);
  return null;
}
